import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchChats } from '../../api/chats';
import { useSidebar } from '../../contexts/SidebarContext';

import ChatsList from './ChatsList';
import ChatArea from './ChatArea';

import MainLayout from '../layout/MainLayout';
import Sidebar from '../layout/Sidebar';
import ContentArea from '../layout/ContentArea';

const ChatsContainer = () => {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [chats, setChats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const { setPage: setSidebarPage } = useSidebar();

  const loadChats = useCallback(async (pageNum = 1) => {
    try {
      setError(null);
      const result = await fetchChats(pageNum);

      setChats(prev => pageNum === 1 ? result.chats : [...prev, ...result.chats]);
      setHasMore(result.hasMore);
      setPage(pageNum);
    } catch (error) {
      console.error('Error fetching chats:', error);
      setError('Failed to load chats');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleLoadMore = useCallback(() => {
    return loadChats(page + 1);
  }, [loadChats, page]);

  useEffect(() => {
    setSelectedChatId(chatId === 'new' ? null : chatId);
  }, [chatId]);

  useEffect(() => {
    loadChats();
  }, [loadChats]);

  const currentChat = useMemo(() => {
    return chats.find(chat => chat.id === chatId);
  }, [chats, chatId]);

  const handleNewChat = useCallback(() => {
    setSelectedChatId(null);
    navigate('/chats');
  }, [navigate, setSelectedChatId]);

  useEffect(() => {
    document.title = 'JetChats';
    setSidebarPage('chats');
  }, [setSidebarPage]);

  return (
    <MainLayout>
      <Sidebar handleNewChat={handleNewChat}>
        <ChatsList 
          chats={chats} 
          isLoading={isLoading} 
          error={error} 
          selectedChatId={selectedChatId}
          setSelectedChatId={setSelectedChatId}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
        />
      </Sidebar>
      <ContentArea>
        <ChatArea 
          setChats={setChats}
          setSelectedChatId={setSelectedChatId}
          currentChatTitle={currentChat?.title}
        />
      </ContentArea>
    </MainLayout>
  );
}

export default memo(ChatsContainer);
